import { useEffect, useRef, useState } from "react";
import {
    X,
    ChevronLeft,
    ChevronRight,
    ZoomIn,
    ArrowRight,
} from "lucide-react";

// All photos here are real completed jobs by Call The Handyman.
const PHOTOS = [
    {
        src: "/assets/kitchen_after.jpg",
        category: "Kitchen refit",
        caption: "Full kitchen refit — Dumbarton",
    },
    {
        src: "/assets/kitchen_before.jpg",
        category: "Before",
        caption: "Same kitchen — before",
    },
    {
        src: "/assets/job_flooring.jpg",
        category: "Flooring",
        caption: "Bathroom flooring — before & after",
    },
    {
        src: "/assets/job_tvmount.jpg",
        category: "TV mounting",
        caption: "Wall-mounted TV with cables tidied",
    },
    {
        src: "/assets/job_flatpack.jpg",
        category: "Flat-pack",
        caption: "Outdoor storage box — built on site",
    },
    {
        src: "/assets/job_fencing.jpg",
        category: "Fencing",
        caption: "Timber garden gate fitted",
    },
    {
        src: "/assets/job_roofing.jpg",
        category: "Roofing",
        caption: "Shed roof re-felted",
    },
    {
        src: "/assets/job_electrical.jpg",
        category: "Electrical",
        caption: "Exterior PIR floodlight install",
    },
    {
        src: "/assets/job_electrical2.jpg",
        category: "Electrical",
        caption: "Gable-end security light replacement",
    },
];

export default function PhotoReel() {
    const [index, setIndex] = useState(null);
    const scrollRef = useRef(null);
    const touchStartX = useRef(null);

    const current = index !== null ? PHOTOS[index] : null;
    const showPrev = () =>
        setIndex((i) => (i === null ? null : (i - 1 + PHOTOS.length) % PHOTOS.length));
    const showNext = () =>
        setIndex((i) => (i === null ? null : (i + 1) % PHOTOS.length));

    useEffect(() => {
        if (index === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") setIndex(null);
            else if (e.key === "ArrowLeft") showPrev();
            else if (e.key === "ArrowRight") showNext();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index]);

    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(dx) < 40) return;
        if (dx < 0) showNext();
        else showPrev();
    };

    const scrollBy = (dir) => {
        const el = scrollRef.current;
        if (!el) return;
        const amount = el.clientWidth * 0.85 * dir;
        el.scrollBy({ left: amount, behavior: "smooth" });
    };

    return (
        <section
            id="recent-work"
            data-testid="photo-reel-section"
            className="py-20 md:py-24 bg-white border-y border-slate-100"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div className="max-w-2xl">
                        <div className="text-xs uppercase tracking-[0.2em] text-amber-700 font-bold mb-3">
                            Recent work
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
                            A look at the jobs we&apos;ve been doing.
                        </h2>
                        <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
                            Scroll through some of the work we&apos;ve completed
                            around Dumbarton. Tap any photo to take a closer
                            look.
                        </p>
                    </div>
                    <div className="hidden md:flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => scrollBy(-1)}
                            data-testid="reel-scroll-left"
                            aria-label="Scroll reel left"
                            className="w-11 h-11 rounded-full border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-colors flex items-center justify-center"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => scrollBy(1)}
                            data-testid="reel-scroll-right"
                            aria-label="Scroll reel right"
                            className="w-11 h-11 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors flex items-center justify-center"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div
                    ref={scrollRef}
                    data-testid="reel-track"
                    className="flex gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory pb-6 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 scroll-smooth [scrollbar-width:thin]"
                >
                    {PHOTOS.map((p, i) => (
                        <button
                            key={`${p.src}-${i}`}
                            type="button"
                            onClick={() => setIndex(i)}
                            data-testid={`reel-card-${i}`}
                            className="group snap-start relative shrink-0 w-[78%] sm:w-[44%] md:w-[32%] lg:w-[24%] aspect-[4/5] rounded-2xl overflow-hidden border border-slate-200 shadow-md bg-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all hover:shadow-xl hover:-translate-y-0.5"
                            aria-label={`Open ${p.caption}`}
                        >
                            <img
                                src={p.src}
                                alt={p.caption}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />
                            <div className="absolute top-3 left-3 flex items-center gap-2">
                                <span className="px-2.5 py-1 rounded-md bg-white text-slate-900 text-[10px] font-extrabold uppercase tracking-[0.18em] shadow">
                                    {p.category}
                                </span>
                                <span className="px-2 py-1 rounded-md bg-amber-500 text-white text-[10px] font-extrabold uppercase tracking-[0.16em] shadow">
                                    Real job
                                </span>
                            </div>
                            <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow">
                                <ZoomIn className="w-4 h-4 text-slate-900" />
                            </div>
                            <div className="absolute left-3 right-3 bottom-3 text-white">
                                <div className="font-display font-bold text-base leading-tight">
                                    {p.caption}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>

                <div className="mt-6 flex items-center justify-between gap-4">
                    <div className="text-xs text-slate-500 md:hidden">
                        Swipe to see more →
                    </div>
                    <a
                        href="#contact"
                        data-testid="reel-cta"
                        className="ml-auto inline-flex items-center gap-2 text-sm font-semibold text-slate-900 hover:text-amber-700 transition-colors"
                    >
                        Got a similar job in mind? Get in touch
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </div>
            </div>

            {current && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${current.caption} enlarged`}
                    data-testid="reel-lightbox"
                    className="fixed inset-0 z-[100] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
                    onClick={() => setIndex(null)}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIndex(null);
                        }}
                        data-testid="reel-lightbox-close"
                        aria-label="Close enlarged photo"
                        className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-colors z-10"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            showPrev();
                        }}
                        data-testid="reel-lightbox-prev"
                        aria-label="Previous photo"
                        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-colors z-10"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            showNext();
                        }}
                        data-testid="reel-lightbox-next"
                        aria-label="Next photo"
                        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-colors z-10"
                    >
                        <ChevronRight className="w-6 h-6" />
                    </button>

                    <figure
                        key={index}
                        className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center reveal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={current.src}
                            alt={current.caption}
                            className="max-w-full max-h-[78vh] w-auto h-auto rounded-xl shadow-2xl object-contain select-none"
                            draggable={false}
                        />
                        <figcaption className="mt-4 flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm text-white text-sm">
                            <span className="font-bold uppercase tracking-[0.18em] text-amber-400">
                                {current.category}
                            </span>
                            <span className="opacity-90">{current.caption}</span>
                            <span className="opacity-60 text-xs tabular-nums pl-2 border-l border-white/20">
                                {index + 1} / {PHOTOS.length}
                            </span>
                        </figcaption>
                    </figure>
                </div>
            )}
        </section>
    );
}
